import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import api, { API } from "../../lib/api";
import { Sparkles, Wand2, Plus, Trash2, ExternalLink, Cpu, Users2, GitFork, GitCompareArrows, ShieldCheck, ArrowRight as ArrowRightIcon, Zap, TrendingUp } from "lucide-react";

export default function StudioHub() {
  const [models, setModels] = useState({ mine: [], public: [] });
  const [community, setCommunity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [forkingId, setForkingId] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/ai-models").then((r) => r.data),
      axios.get(`${API}/ai-models-community?limit=24`, { withCredentials: false }).then((r) => r.data),
    ])
      .then(([mine, com]) => {
        setModels(mine || { mine: [], public: [] });
        setCommunity(com?.models || []);
      })
      .catch((e) => setErr(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Delete this model? This cannot be undone.")) return;
    try {
      await api.delete(`/ai-models/${id}`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Delete failed");
    }
  };

  const fork = async (id) => {
    setForkingId(id);
    try {
      const r = await api.post(`/ai-models/${id}/fork`);
      // reload + navigate handled by StudioModel; here we just refresh the hub
      load();
      alert(`Forked to your Studio as "${r.data.name}". Open it from "Your models" below.`);
    } catch (e) {
      alert(e.response?.data?.detail || "Fork failed");
    } finally {
      setForkingId(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-200 border-2 border-slate-900 font-heading font-semibold text-xs uppercase tracking-widest mb-3">
            <Wand2 size={12} strokeWidth={2.5} /> Model Studio
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold leading-tight" data-testid="studio-title">
            Build your own <span className="bg-amber-200 px-2 border-2 border-slate-900 inline-block rotate-[-1deg]">models</span>
          </h1>
          <p className="text-slate-600 mt-3 max-w-2xl">
            Compose a bespoke AI from a base brain, guardrails, and few-shot examples — or import any of the 200,000+ open models on Hugging Face.
            Both become first-class brains, usable in Compare, Debate, and Workflows.
          </p>
        </div>
        {models.mine.length >= 2 && (
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/trending" className="brut-btn text-sm bg-white text-slate-900" data-testid="trending-link">
              <TrendingUp size={12} strokeWidth={3} /> Trending
            </Link>
            <Link to="/creators" className="brut-btn text-sm bg-white text-slate-900" data-testid="creators-link">
              <Users2 size={12} strokeWidth={3} /> Discover creators
            </Link>
            <Link to="/studio/fine-tune" className="brut-btn text-sm bg-white text-slate-900" data-testid="fine-tune-link">
              <Zap size={12} strokeWidth={3} /> Fine-tune
            </Link>
            <Link to="/studio/diff" className="brut-btn text-sm" data-testid="diff-link">
              <GitCompareArrows size={12} strokeWidth={3} /> Compare two models
            </Link>
          </div>
        )}
        {models.mine.length < 2 && (
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/trending" className="brut-btn text-sm bg-white text-slate-900" data-testid="trending-link">
              <TrendingUp size={12} strokeWidth={3} /> Trending
            </Link>
            <Link to="/creators" className="brut-btn text-sm bg-white text-slate-900" data-testid="creators-link">
              <Users2 size={12} strokeWidth={3} /> Discover creators
            </Link>
            <Link to="/studio/fine-tune" className="brut-btn text-sm bg-white text-slate-900" data-testid="fine-tune-link">
              <Zap size={12} strokeWidth={3} /> Fine-tune
            </Link>
          </div>
        )}
      </div>

      {/* Three paths */}
      <div className="grid md:grid-cols-3 gap-4">
        <Link
          to="/studio/new/composed"
          className="brut-card p-6 hover:-translate-y-1 hover:translate-x-0 transition-transform relative overflow-hidden"
          style={{ boxShadow: "6px 6px 0 #F59E0B" }}
          data-testid="path-composed"
        >
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-amber-200 opacity-30" />
          <div className="relative">
            <div className="w-14 h-14 rounded-2xl border-2 border-slate-900 bg-amber-200 flex items-center justify-center text-3xl mb-3">🎓</div>
            <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-500 mb-1">Path A</div>
            <h2 className="font-heading text-2xl font-bold mb-2">Compose from a brain</h2>
            <p className="text-sm text-slate-600 mb-4">
              Pick a base brain, layer in a mission, tone, few-shot examples, and guardrails. Ships as a reusable model everywhere on the platform.
            </p>
            <div className="inline-flex items-center gap-1 font-heading font-bold text-slate-900 uppercase tracking-widest text-xs">
              Start composing <Sparkles size={12} strokeWidth={2.5} />
            </div>
          </div>
        </Link>

        <Link
          to="/studio/new/hf"
          className="brut-card p-6 hover:-translate-y-1 hover:translate-x-0 transition-transform relative overflow-hidden"
          style={{ boxShadow: "6px 6px 0 #EA580C" }}
          data-testid="path-hf"
        >
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-orange-200 opacity-30" />
          <div className="relative">
            <div className="w-14 h-14 rounded-2xl border-2 border-slate-900 bg-orange-200 flex items-center justify-center text-3xl mb-3">🤗</div>
            <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-500 mb-1">Path B</div>
            <h2 className="font-heading text-2xl font-bold mb-2">Import from Hugging Face</h2>
            <p className="text-sm text-slate-600 mb-4">
              Search the HF Hub, pick any text-generation model (Llama, Qwen, Mistral, Phi, or your own fine-tune), paste your HF token — it becomes a first-class Brain.
            </p>
            <div className="inline-flex items-center gap-1 font-heading font-bold text-slate-900 uppercase tracking-widest text-xs">
              Browse the Hub <ExternalLink size={12} strokeWidth={2.5} />
            </div>
          </div>
        </Link>

        <Link
          to="/studio/new/custom"
          className="brut-card p-6 hover:-translate-y-1 hover:translate-x-0 transition-transform relative overflow-hidden"
          style={{ boxShadow: "6px 6px 0 #DC2626" }}
          data-testid="path-custom"
        >
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-red-200 opacity-30" />
          <div className="relative">
            <div className="w-14 h-14 rounded-2xl border-2 border-slate-900 bg-red-200 flex items-center justify-center text-3xl mb-3">🔥</div>
            <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-500 mb-1">Path C</div>
            <h2 className="font-heading text-2xl font-bold mb-2">Bring your endpoint</h2>
            <p className="text-sm text-slate-600 mb-4">
              Plug in Fireworks, Together, Groq, or your own vLLM/Ollama server — any OpenAI-compatible endpoint. Verified live before saving.
            </p>
            <div className="inline-flex items-center gap-1 font-heading font-bold text-slate-900 uppercase tracking-widest text-xs">
              Add custom brain <ArrowRightIcon />
            </div>
          </div>
        </Link>
      </div>

      {err && <div className="brut-card p-4 text-red-600 text-sm">{err}</div>}

      {/* My models */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-xl font-bold flex items-center gap-2">
            <Cpu size={18} strokeWidth={2.5} /> Your models
            <span className="text-xs bg-slate-100 border-2 border-slate-900 rounded-full px-2 py-0.5 font-mono">{models.mine.length}</span>
          </h3>
        </div>
        {loading ? (
          <div className="italic text-slate-400 text-sm">Loading…</div>
        ) : models.mine.length === 0 ? (
          <div className="brut-card p-8 text-center">
            <div className="text-4xl mb-2">🪄</div>
            <div className="font-heading font-bold text-lg mb-1">Your studio is empty</div>
            <p className="text-sm text-slate-600 mb-4">Compose your first model or import one from Hugging Face.</p>
            <Link to="/studio/new/composed" className="brut-btn text-sm" data-testid="empty-cta">
              <Plus size={14} strokeWidth={2.5} /> Compose one
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="my-models-grid">
            {models.mine.map((m) => <ModelCard key={m.ai_model_id} m={m} onDelete={remove} />)}
          </div>
        )}
      </section>

      {/* Community marketplace */}
      {community.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-3">
            <h3 className="font-heading text-xl font-bold flex items-center gap-2">
              <Users2 size={18} strokeWidth={2.5} /> Community marketplace
              <span className="text-xs bg-slate-100 border-2 border-slate-900 rounded-full px-2 py-0.5 font-mono">{community.length}</span>
            </h3>
            <span className="text-xs text-slate-500 italic">Fork any of these into your own Studio in one click.</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="community-grid">
            {community.map((m) => (
              <CommunityCard key={m.ai_model_id} m={m} onFork={fork} forking={forkingId === m.ai_model_id} />
            ))}
          </div>
        </section>
      )}

      {/* Old public list — hidden if community marketplace shows */}
      {community.length === 0 && models.public.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-3">
            <h3 className="font-heading text-xl font-bold flex items-center gap-2">
              <Users2 size={18} strokeWidth={2.5} /> Community models
              <span className="text-xs bg-slate-100 border-2 border-slate-900 rounded-full px-2 py-0.5 font-mono">{models.public.length}</span>
            </h3>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="public-models-grid">
            {models.public.map((m) => <ModelCard key={m.ai_model_id} m={m} readOnly />)}
          </div>
        </section>
      )}
    </div>
  );
}

function CommunityCard({ m, onFork, forking }) {
  const grade = m.last_grade?.average?.overall;
  return (
    <div
      className="brut-card p-4 flex flex-col gap-3 relative overflow-hidden"
      style={{ boxShadow: `3px 3px 0 #F59E0B` }}
      data-testid={`community-card-${m.ai_model_id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl border-2 border-slate-900 bg-amber-100 flex items-center justify-center text-2xl shrink-0">{m.avatar}</div>
        <div className="flex-1 min-w-0">
          <Link to={`/studio/${m.ai_model_id}`} className="block hover:text-amber-700">
            <div className="font-heading font-bold text-base leading-tight truncate">{m.name}</div>
          </Link>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">by {m.author || "someone"}</div>
        </div>
      </div>
      {m.description && <p className="text-xs text-slate-600 line-clamp-2">{m.description}</p>}
      <div className="flex items-center gap-2 flex-wrap text-[10px]">
        {m.fork_count > 0 && (
          <span className="px-1.5 py-0.5 rounded-full bg-slate-900 text-white font-heading font-bold uppercase tracking-widest inline-flex items-center gap-1">
            <GitFork size={9} strokeWidth={3} /> {m.fork_count}
          </span>
        )}
        {grade !== undefined && (
          <span className="px-1.5 py-0.5 rounded-full font-heading font-bold uppercase tracking-widest inline-flex items-center gap-1"
                style={{ backgroundColor: gradeColor(grade) + "22", color: gradeColor(grade), border: `1px solid ${gradeColor(grade)}` }}>
            <ShieldCheck size={9} strokeWidth={3} /> {grade}
          </span>
        )}
        <span className="text-slate-500 font-mono truncate">{m.base_brain_id}</span>
      </div>
      <button
        onClick={() => onFork(m.ai_model_id)}
        disabled={forking}
        className="brut-btn text-xs w-full mt-auto disabled:opacity-40"
        data-testid={`fork-${m.ai_model_id}`}
      >
        <GitFork size={11} strokeWidth={3} /> {forking ? "Forking…" : "Fork to my Studio"}
      </button>
    </div>
  );
}

function gradeColor(v) {
  if (v >= 80) return "#10B981";
  if (v >= 50) return "#F59E0B";
  return "#EF4444";
}

function ModelCard({ m, onDelete, readOnly }) {
  const kindColor = m.kind === "composed" ? "#F59E0B" : "#EA580C";
  return (
    <div
      className="brut-card p-4 flex flex-col gap-3"
      style={{ boxShadow: `3px 3px 0 ${kindColor}` }}
      data-testid={`model-card-${m.ai_model_id}`}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-11 h-11 rounded-xl border-2 border-slate-900 flex items-center justify-center text-2xl shrink-0"
          style={{ backgroundColor: kindColor + "22" }}
        >
          {m.avatar}
        </div>
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-heading font-bold px-1.5 py-0.5 rounded-full border-2 mb-0.5"
               style={{ borderColor: kindColor, color: kindColor, backgroundColor: kindColor + "1F" }}>
            {m.kind === "composed" ? "Composed" : "HF Import"}
          </div>
          <div className="font-heading font-bold text-base leading-tight truncate">{m.name}</div>
          <div className="text-[11px] text-slate-500 truncate">
            {m.kind === "composed" ? m.base_brain_id : m.hf_repo_id}
          </div>
        </div>
      </div>
      {m.description && <p className="text-xs text-slate-600 line-clamp-2">{m.description}</p>}
      <div className="flex items-center justify-between text-xs mt-auto pt-2 border-t border-slate-100">
        <Link
          to={`/studio/${m.ai_model_id}`}
          className="font-heading font-bold text-slate-900 uppercase tracking-widest hover:text-amber-600"
          data-testid={`open-${m.ai_model_id}`}
        >
          Open →
        </Link>
        {!readOnly && (
          <button
            onClick={() => onDelete(m.ai_model_id)}
            className="text-red-500 hover:text-red-700"
            title="Delete"
            data-testid={`delete-${m.ai_model_id}`}
          >
            <Trash2 size={13} strokeWidth={2.5} />
          </button>
        )}
      </div>
    </div>
  );
}
